import type { ContentBlock } from '@shared/types';
import type { ChecklistState } from '../adapters/persistence';

type Props = {
  blocks: ContentBlock[];
  checklistId?: string;
  checklistState?: ChecklistState;
  onToggleChecklist?: (itemKey: string, checked: boolean) => void;
  /** When TTS is speaking, highlight matching fragment text if present. */
  activeFragmentId?: string | null;
};

export function ContentBlocks({
  blocks,
  checklistId,
  checklistState,
  onToggleChecklist,
  activeFragmentId,
}: Props) {
  return (
    <div className="content-stack">
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`;
        const fragmentHint =
          activeFragmentId && 'text' in block && typeof block.text === 'string'
            ? activeFragmentId
            : null;
        const highlight =
          Boolean(fragmentHint) &&
          'text' in block &&
          typeof block.text === 'string' &&
          // fragment ids look like `p-0`, `title`, etc. — soft match by index
          (activeFragmentId === `block-${index}` ||
            activeFragmentId === `p-${index}` ||
            (activeFragmentId?.startsWith('block-') === false &&
              index === Number(activeFragmentId?.replace(/\D/g, ''))));
        switch (block.type) {
          case 'heading':
            return (
              <h3
                key={key}
                className={`block-heading${highlight ? ' speech-highlight' : ''}`}
              >
                {block.text}
              </h3>
            );
          case 'paragraph':
            return (
              <p
                key={key}
                className={`block-p${highlight ? ' speech-highlight' : ''}`}
                data-fragment-active={highlight ? 'true' : undefined}
              >
                {block.text}
              </p>
            );
          case 'tip':
            return (
              <div key={key} className="block-tip">
                {block.text}
              </div>
            );
          case 'warning':
            return (
              <div key={key} className="block-warning">
                {block.text}
              </div>
            );
          case 'step-list':
            return (
              <ol key={key} className="block-list">
                {block.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            );
          case 'checklist': {
            const id = checklistId ?? block.id;
            return (
              <div key={key} className="block-checklist">
                {block.title ? <strong>{block.title}</strong> : null}
                {block.items.map((item, itemIndex) => {
                  const itemKey = `${id}:${itemIndex}`;
                  const checked = Boolean(checklistState?.[itemKey]);
                  return (
                    <label key={itemKey}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) =>
                          onToggleChecklist?.(itemKey, event.target.checked)
                        }
                      />
                      <span>{item}</span>
                    </label>
                  );
                })}
              </div>
            );
          }
          case 'image':
            return (
              <figure key={key}>
                <img src={block.source} alt={block.alt} style={{ maxWidth: '100%' }} />
                {block.caption ? <figcaption>{block.caption}</figcaption> : null}
              </figure>
            );
          case 'diagram':
            return (
              <div key={key} className="block-tip">
                {block.fallbackText ?? block.alt}
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
